import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, ChevronRight, ChevronDown, Check } from "lucide-react";
import { useState } from "react";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ScrollReveal";
import { servicesData } from "@/data/services";
import { caseStudies } from "@/data/caseStudies";
import SEOHead from "@/components/SEOHead";

const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/5 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        data-testid={`faq-toggle-${question.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}
        className="w-full flex items-center justify-between p-5 text-left group"
      >
        <span className="font-heading text-base font-semibold text-white pr-4 group-hover:text-teal-bright transition-colors duration-200">
          {question}
        </span>
        <ChevronDown
          size={18}
          className={`text-slate-600 flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180 text-teal-bright" : ""}`}
        />
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <p className="px-5 pb-5 text-sm text-slate-400 leading-relaxed">{answer}</p>
      </motion.div>
    </div>
  );
};

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const service = servicesData.find((s) => s.slug === slug);

  if (!service) return <Navigate to="/services" replace />;

  const Icon = service.icon;
  const relatedStudies = service.relatedCaseStudies
    .map((cs) => caseStudies.find((c) => c.slug === cs))
    .filter(Boolean);

  return (
    <main data-testid={`service-detail-${slug}`}>
      <SEOHead
        title={`${service.title} — Venury`}
        description={service.shortDesc}
        url={`https://venury.tech/services/${slug}`}
      />
      {/* Hero */}
      <section className="relative min-h-[65vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-[#0B1116]" />
        <div className="hero-orb-teal" style={{ top: "-10%", right: "5%" }} />
        <div className="hero-orb-orange" style={{ bottom: "-20%", left: "-10%" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1116]/40 to-[#0B1116]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 w-full">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6">
            <Link to="/services" className="inline-flex items-center gap-2 font-mono text-sm text-slate-400 hover:text-teal-bright transition-colors duration-200">
              <ArrowLeft size={14} /> All Services
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="w-16 h-16 rounded-xl bg-teal-primary/10 border border-teal-primary/20 flex items-center justify-center mb-6"
          >
            <Icon size={32} className="text-teal-bright" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight max-w-4xl leading-[1.1] mb-6"
          >
            {service.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-2xl mb-10"
          >
            {service.heroSubtitle}
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="flex flex-wrap gap-6"
          >
            {service.stats.map((stat, i) => (
              <span key={i} className="font-mono text-sm text-slate-400 border border-white/5 px-4 py-2 rounded-lg">
                {stat}
              </span>
            ))}
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-10"
          >
            <Link
              to="/contact"
              data-testid="service-detail-cta"
              className="inline-flex items-center gap-3 bg-teal-primary hover:bg-teal-dark text-white font-bold text-base px-8 py-4 rounded transition-colors duration-200"
            >
              Schedule Consultation <ArrowUpRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Problem */}
      <section data-testid="problem-section" className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute top-1/2 right-[8%] -translate-y-1/2 w-80 h-80 bg-teal-primary/8 rounded-full blur-[80px] pointer-events-none" />
        <div className="absolute top-[10%] right-[6%] w-48 h-48 border-2 border-teal-primary/10 rounded-2xl rotate-12 pointer-events-none" />
        <div className="absolute top-[50%] right-[16%] w-24 h-24 border-2 border-orange-primary/10 rounded-full pointer-events-none" />
        <div className="absolute bottom-[15%] right-[10%] w-12 h-12 bg-teal-primary/15 rounded-lg rotate-45 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <ScrollReveal>
            <span className="font-mono text-sm text-orange-bright uppercase tracking-widest block mb-4">The Problem</span>
            <div className="space-y-6 max-w-3xl">
              {service.problem.map((p, i) => (
                <p key={i} className="text-base md:text-lg text-slate-400 leading-relaxed">{p}</p>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Solution */}
      <section data-testid="service-solution" className="py-20 md:py-28 bg-[#080C10]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="font-mono text-sm text-teal-bright uppercase tracking-widest block mb-4">Our Solution</span>
            <p className="text-base md:text-lg text-slate-400 leading-relaxed max-w-3xl mb-16">{service.solutionOverview}</p>
          </ScrollReveal>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" staggerDelay={0.08}>
            {service.features.map((feature, i) => (
              <StaggerItem key={i}>
                <div
                  data-testid={`feature-card-${i}`}
                  className="glass-card rounded-lg p-6 h-full hover:border-teal-primary/30 transition-colors duration-300"
                >
                  <h3 className="font-heading text-base font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{feature.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute bottom-0 left-[5%] w-64 h-64 bg-orange-primary/5 rounded-full blur-[80px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <ScrollReveal>
            <span className="font-mono text-sm text-orange-bright uppercase tracking-widest block mb-4">Technology</span>
            <h2 className="font-heading text-3xl font-bold text-white tracking-tight mb-8">Tech Stack</h2>
            <div className="flex flex-wrap gap-3">
              {service.techStack.map((tech) => (
                <span key={tech} className="font-mono text-sm px-4 py-2 rounded-lg bg-navy-base/50 border border-white/5 text-slate-400 hover:text-teal-bright hover:border-teal-primary/30 transition-colors duration-200">
                  {tech}
                </span>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Related Case Studies */}
      {relatedStudies.length > 0 && (
        <section data-testid="related-studies" className="py-20 md:py-28 bg-[#080C10]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <ScrollReveal>
              <span className="font-mono text-sm text-teal-bright uppercase tracking-widest block mb-4">Related Work</span>
              <h2 className="font-heading text-3xl font-bold text-white tracking-tight mb-12">Case Studies</h2>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {relatedStudies.map((cs) => (
                <ScrollReveal key={cs.slug}>
                  <Link
                    to={`/work/${cs.slug}`}
                    data-testid={`related-${cs.slug}`}
                    className="block glass-card rounded-lg overflow-hidden group h-full flex flex-col"
                  >
                    <div className="relative h-48 overflow-hidden flex-shrink-0">
                      <img src={cs.image} alt={cs.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1a252f] to-transparent" />
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="font-heading text-lg font-semibold text-white mb-2 group-hover:text-teal-bright transition-colors duration-200">{cs.title}</h3>
                      <p className="text-sm text-slate-400 mb-4 flex-1">{cs.shortDesc}</p>
                      <span className="inline-flex items-center gap-1 font-mono text-xs text-teal-bright uppercase tracking-wider">
                        View Case Study <ChevronRight size={12} />
                      </span>
                    </div>
                  </Link>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section data-testid="service-faq" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="font-mono text-sm text-teal-bright uppercase tracking-widest block mb-4">FAQ</span>
            <h2 className="font-heading text-3xl font-bold text-white tracking-tight mb-10">
              Frequently Asked Questions
            </h2>
          </ScrollReveal>

          <StaggerContainer className="space-y-3 max-w-3xl" staggerDelay={0.1}>
            {service.faq.map((item, i) => (
              <StaggerItem key={i}>
                <FaqItem question={item.q} answer={item.a} />
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>
    </main>
  );
}
